import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  timer,
} from 'rxjs';
import { environment } from '@env/environment';
import { Router } from '@angular/router';

/**
 * Represents the maintenance status of the application.
 *
 * @example
 * ```typescript
 * // Active maintenance
 * const activeStatus: MaintenanceStatus = {
 *   maintenance: true,
 *   message: { de: 'Wartung', en: 'Maintenance' },
 *   endsAt: '2024-01-01T12:00:00Z',
 *   updatedAt: '2024-01-01T10:00:00Z'
 * };
 *
 * // Normal operation
 * const normalStatus: MaintenanceStatus = {
 *   maintenance: false,
 *   updatedAt: '2024-01-01T10:00:00Z'
 * };
 * ```
 */
export type MaintenanceStatus =
  | {
      /** Indicates maintenance mode is active */
      maintenance: true;
      /** Localized maintenance messages */
      message: {
        de: string;
        en: string;
      };
      /** When maintenance is scheduled to end */
      endsAt: string | Date;
      /** When this status was last updated */
      updatedAt: string | Date;
    }
  | {
      /** Indicates normal operation */
      maintenance: false;
      /** When this status was last updated */
      updatedAt: string | Date;
    };

/**
 * Service for managing application maintenance status and automatic redirects.
 *
 * Features:
 * - Polls maintenance status every 60 seconds
 * - Provides reactive streams for status updates
 * - Automatically redirects users during maintenance activation
 * - Stores user's last URL for post-maintenance redirect
 * - Handles HTTP errors gracefully
 *
 * The service automatically starts polling when `load()` is called and
 * manages all subscriptions internally.
 *
 * @example
 * ```typescript
 * // In app initialization
 * constructor(private maintenance: MaintenanceService) {
 *   this.maintenance.load();
 * }
 *
 * // Check current status
 * if (this.maintenance.currentStatus?.maintenance) {
 *   console.log('Maintenance active');
 * }
 *
 * // Subscribe to status changes
 * this.maintenance.status$.subscribe(status => {
 *   console.log('Status updated:', status);
 * });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class MaintenanceService implements OnDestroy {
  /** Internal subject for maintenance status state management */
  private readonly _statusSubject$ =
    new BehaviorSubject<MaintenanceStatus | null>(null);

  /** Subscription for the polling timer */
  private _pollingSubscription?: Subscription;

  /** Subscription for the maintenance redirect logic */
  private _redirectSubscription?: Subscription;

  /**
   * Observable stream of maintenance status updates.
   * Emits null initially, then MaintenanceStatus objects as they're fetched.
   */
  public readonly status$: Observable<MaintenanceStatus | null> =
    this._statusSubject$.asObservable();

  /**
   * Observable stream indicating whether maintenance mode is currently active.
   * Filters out null values and maps to boolean maintenance status.
   * Used by guards and components for reactive maintenance detection.
   */
  public readonly isMaintenanceMode$: Observable<boolean> = this.status$.pipe(
    filter(status => status !== null),
    map(status => status!.maintenance ?? false)
  );

  constructor(
    private readonly _http: HttpClient,
    private readonly _router: Router
  ) {}

  /**
   * Initializes the maintenance service by starting status polling and redirect handling.
   * Should be called once during application bootstrap.
   *
   * Sets up:
   * - 60-second polling interval for status updates
   * - Automatic redirect logic when maintenance is activated
   */
  load(): void {
    this._startPolling();
    this._setupMaintenanceRedirect();
  }

  /**
   * Starts the polling mechanism for maintenance status updates.
   * Polls immediately, then every 60 seconds thereafter.
   *
   * Error handling:
   * - Logs HTTP errors to console
   * - Returns null on error to maintain stream continuity
   * - Does not interrupt polling on individual request failures
   */
  private _startPolling(): void {
    this._pollingSubscription = timer(0, 60000)
      .pipe(
        switchMap(() => this._fetchStatus()),
        catchError(err => {
          console.error('Maintenance check failed:', err);
          return of(null);
        })
      )
      .subscribe(status => this._statusSubject$.next(status));
  }

  /**
   * Fetches the current maintenance status from the API endpoint.
   *
   * @returns {Observable<MaintenanceStatus | null>} Observable that emits the maintenance status or null on error
   */
  private _fetchStatus(): Observable<MaintenanceStatus | null> {
    return this._http.get<MaintenanceStatus>(
      environment.MAINTENANCE_STATUS_ENDPOINT
    );
  }

  /**
   * Gets the current maintenance status synchronously.
   *
   * @returns The current maintenance status or null if not yet loaded
   *
   * @example
   * ```typescript
   * const status = maintenanceService.currentStatus;
   * if (status?.maintenance) {
   *   console.log('Maintenance is active');
   * }
   * ```
   */
  public get currentStatus(): MaintenanceStatus | null {
    return this._statusSubject$.value;
  }

  /**
   * Sets up automatic redirect logic when maintenance mode is activated.
   *
   * Behavior:
   * - Monitors maintenance mode changes using distinctUntilChanged
   * - Stores current URL in sessionStorage before redirecting
   * - Only redirects if not already on maintenance page
   * - Prevents redirect loops and preserves user's original location
   *
   * The stored URL is used by MaintenanceComponent to redirect back
   * when maintenance ends.
   */
  private _setupMaintenanceRedirect(): void {
    this._redirectSubscription = this.isMaintenanceMode$
      .pipe(distinctUntilChanged())
      .subscribe(isMaintenanceMode => {
        if (isMaintenanceMode && !this._router.url.includes('/maintenance')) {
          // Store current URL before redirecting
          sessionStorage.setItem('lastUrlBeforeMaintenance', this._router.url);
          void this._router.navigate(['/maintenance']);
        }
      });
  }

  /**
   * Cleanup method called when the service is destroyed.
   * Unsubscribes from all active subscriptions to prevent memory leaks.
   *
   * Note: Since this is a root-provided service, this will typically
   * only be called when the application is destroyed.
   */
  ngOnDestroy(): void {
    this._pollingSubscription?.unsubscribe();
    this._redirectSubscription?.unsubscribe();
  }
}
